import { GameConfiguration, GuidedGame, GuidedGameStep } from './types';
import { vinhosModuleGuidedSteps } from './moduleContent';
import { reserve2010FirstGameSteps } from './reserve-2010';
import { filterByConfiguration } from './contentFilter';

/**
 * Tryb „Pierwsza partia” — przygotowanie i decyzje przed rozgrywką.
 * Osobny od prowadzonego samouczka (guidedGame), który prowadzi przez samą partię.
 * Kroki modułów pojawiają się tylko przy aktywnej konfiguracji.
 */
export const vinhosFirstGame: GuidedGame = {
  steps: [
    {
      id: 'fg-variant',
      title: 'Wybór wariantu',
      summary:
        'Ustaw Special Vintage 2016 i wyłącz wszystkie opcjonalne moduły.',
      checklist: [
        'Odwróć planszę na stronę Special Vintage 2016.',
        'Użyj tej samej strony plansz graczy.',
        'Wyłącz Experts, Islands, Tasting Room i Connoisseur.',
        'Nie mieszaj zasad z Reserve 2010.',
      ],
      whyTitle: 'Dlaczego to robimy?',
      whyContent:
        'Wariant 2016 jest krótszy i czytelniejszy, a zachowuje rdzeń ekonomiczny gry. Mieszanie szczegółów z Reserve 2010 prowadzi do błędów.',
      commonMistake: 'Rozpoczęcie nauki na stronie Reserve 2010 albo z włączonymi modułami.',
      chapterId: 'before-you-start',
    },
    {
      id: 'fg-goal',
      title: 'Cel pierwszej partii',
      summary:
        'Wygrywa gracz z największą liczbą VP po sześciu latach i trzech targach.',
      checklist: [
        'Wyjaśnij różnicę między bagos a VP.',
        'Wskaż eksport jako główne źródło bezpośrednich punktów.',
        'Wskaż targi jako źródło punktów i przywilejów.',
      ],
      whyTitle: 'Dlaczego to robimy?',
      whyContent:
        'Bez jasnego celu gracze maksymalizują pieniądze zamiast budować eksport i jakość na targi.',
      commonMistake: 'Traktowanie bagos jak punktów zwycięstwa 1:1.',
      chapterId: 'game-goal',
    },
    {
      id: 'fg-components',
      title: 'Elementy i obszary planszy',
      summary:
        'Nazwij Quadrel, tory, regiony, sprzedaż, eksport i targi zanim ktoś wykona akcję.',
      checklist: [
        'Pokaż Quadrel 3×3 i tor kolejności.',
        'Wskaż regiony oraz rynki sprzedaży i eksportu.',
        'Pokaż planszę gracza: posiadłości, magazyn/piwnica, bank.',
      ],
      whyTitle: 'Dlaczego to robimy?',
      whyContent:
        'Większość błędów wynika z mylenia wartości produkcji, wartości wina i punktów targowych.',
      commonMistake: 'Pomijanie różnicy między wartością produkcji a wartością konkretnego wina.',
      chapterId: 'board-overview',
    },
    {
      id: 'fg-setup',
      title: 'Przygotowanie stołu',
      summary:
        'Rozłóż komponenty zgodnie z liczbą graczy i diagramem przygotowania.',
      checklist: [
        'Ogranicz pola sprzedaży i eksportu do liczby graczy.',
        'Usuń nadmiar winiarni i piwnic.',
        'Rozłóż regiony, ekspertów, kafle roczników i targi.',
        'Ustaw znaczniki graczy i kolejność początkową.',
      ],
      whyTitle: 'Dlaczego to robimy?',
      whyContent:
        'Limity kafli i pól są częścią balansu — pełna pula zniekształca pierwszą partię.',
      commonMistake: 'Pozostawienie wszystkich kafli dostępnych niezależnie od liczby graczy.',
      chapterId: 'setup',
    },
    {
      id: 'fg-year',
      title: 'Struktura roku',
      summary:
        'Każdy rok ma dwie główne akcje, utrzymanie, starzenie, produkcję i ewentualne targi.',
      checklist: [
        'Wyjaśnij kolejność: akcje → kolejność → utrzymanie → starzenie → produkcja.',
        'Zaznacz targi po 3., 5. i 6. roku.',
        'Podkreśl, że darmowe efekty nie dają dodatkowej głównej akcji.',
      ],
      whyTitle: 'Dlaczego to robimy?',
      whyContent:
        'Gracze często gubią kolejność końca roku albo myślą, że mają więcej niż dwie akcje.',
      commonMistake: 'Produkcja przed starzeniem albo pominięcie utrzymania sprzedaży.',
      chapterId: 'year-structure',
    },
    {
      id: 'fg-quadrel',
      title: 'Ruch i dziewięć pól',
      summary:
        'Naucz kosztów ruchu zanim ktoś wybierze pierwszą akcję.',
      checklist: [
        'Ruch sąsiadujący jest darmowy; dalszy kosztuje 1 bago.',
        'Płać każdemu graczowi już stojącemu na polu.',
        'Pass / Press Release nie generuje kosztów wejścia.',
        'Nie wchodź na pole, jeśli nie możesz zapłacić i wykonać akcji.',
      ],
      whyTitle: 'Dlaczego to robimy?',
      whyContent:
        'Koszt pozycjonowania jest kluczową interakcją Special Vintage 2016.',
      commonMistake: 'Pozostanie na tym samym polu albo pominięcie opłat dla innych graczy.',
      chapterId: 'main-actions',
    },
    {
      id: 'fg-first-year',
      title: 'Jak poprowadzić pierwszy rok',
      summary:
        'Skup się na rozwoju posiadłości i płynności, nie na wczesnym eksporcie słabego wina.',
      checklist: [
        'Każdy wykonuje pierwszą akcję rozwojową.',
        'Aktualizuj kolejność tylko po Pass / Press Release.',
        'Druga akcja: produkcja lub gotówka, nie chaotyczny eksport.',
        'Na głos przejdź utrzymanie, starzenie i produkcję.',
      ],
      whyTitle: 'Dlaczego to robimy?',
      whyContent:
        'Pierwszy rok uczy rytmu gry; zbyt wczesny eksport słabych win uczy złych nawyków.',
      commonMistake: 'Kupowanie wszystkiego bez rezerwy na ruch i kluczową akcję.',
      chapterId: 'guided-first-round',
    },
    {
      id: 'fg-strategy',
      title: 'Bezpieczne zasady dla początkujących',
      summary:
        'Zbuduj jedną sprawną posiadłość, łącz sprzedaż z eksportem i planuj targi.',
      checklist: [
        'Najpierw jedna produkująca posiadłość, potem ekspansja.',
        'Zostaw rezerwę bagos na ruch.',
        'Piwnica ma sens dopiero przy dojrzewaniu.',
        'Nie odkładaj wszystkich decyzji targowych na ostatnią chwilę.',
      ],
      whyTitle: 'Dlaczego to robimy?',
      whyContent:
        'Proste zasady ograniczają paraliż decyzyjny w pierwszej partii.',
      commonMistake: 'Rozpraszanie się na wiele regionów bez działającej produkcji.',
      chapterId: 'beginner-strategy',
    },
    {
      id: 'fg-mistakes',
      title: 'Najczęstsze błędy',
      summary:
        'Przejrzyj listę kontrolną błędów zanim zaczniecie właściwą partię.',
      checklist: [
        'Nie zostawaj na tym samym polu Quadrelu (poza wyjątkiem Pass).',
        'Najpierw starzenie, potem produkcja.',
        'Przy utrzymaniu zabierz dokładnie jedną beczkę ze sprzedaży.',
        'Na targi wystaw dokładnie jedno wino.',
      ],
      whyTitle: 'Dlaczego to robimy?',
      whyContent:
        'Te błędy wracają w prawie każdej pierwszej partii i psują poczucie sprawiedliwości zasad.',
      commonMistake: 'Liczenie pieniędzy jak VP bez podstawy na planszy.',
      chapterId: 'common-mistakes',
    },
    ...vinhosModuleGuidedSteps.map(
      (step): GuidedGameStep => ({
        ...step,
        id: `fg-${step.id}`,
      })
    ),
  ]
    .map((step) =>
      step.requiredVariantIds?.length
        ? step
        : { ...step, requiredVariantIds: ['special-vintage-2016' as const] }
    )
    .concat(reserve2010FirstGameSteps),
};

export function filterFirstGameStepsForModules(
  activeModuleIds: string[] | GameConfiguration
): GuidedGameStep[] {
  if (!Array.isArray(activeModuleIds)) {
    return filterByConfiguration(vinhosFirstGame.steps, activeModuleIds);
  }
  return vinhosFirstGame.steps.filter(
    (s) =>
      (!s.requiredModuleIds?.length ||
        s.requiredModuleIds.every((id) => activeModuleIds.includes(id))) &&
      (!s.requiredVariantIds?.length ||
        s.requiredVariantIds.includes('special-vintage-2016'))
  );
}

export function filterFirstGameStepsForConfiguration(config: GameConfiguration) {
  return filterByConfiguration(vinhosFirstGame.steps, config);
}

export function calculateFirstGamePercent(
  completedStepIds: string[],
  totalSteps?: number
): number {
  const total = totalSteps ?? vinhosFirstGame.steps.length;
  return total
    ? Math.round((completedStepIds.length / total) * 100)
    : 0;
}
